import type { FC, PropsWithChildren } from 'react';
import { createContext, useContext, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SlideOut } from '@interstate/components/SlideOut';
import { ScopedStateProvider } from '@makemydeal/shared-scoped-store';
import { Typography } from '@interstate/components/Typography';
import { Box } from '@interstate/components/Box';
import { Button } from '@interstate/components/Button';
import { AiLogoIcon } from '@makemydeal/dr-shared-components';
import { AiVehicleRecommendationSlideout } from './AiVehicleRecommendationSlideout';
import { nanoid } from 'nanoid';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { AccelerateVehicleCondition } from '@makemydeal/dr-platform-types';
import { scopedActionsCreators, scopedActions } from '@makemydeal/shared-scoped-store';
import * as paymentInputActionCreators from '@makemydeal/dr-dash-store/actions/paymentInput';
import * as deskingActionCreators from '@makemydeal/dr-dash-store/actions/deskingActionCreators';
import { dealLockHooks } from '@makemydeal/dr-dash-components';
import { getVehicle } from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { clickAlternativeVehicleOptionsLink } from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';

interface AiVehicleRecommendationContextValue {
    showRecommendations: (vehicles: any[], scopeId: string) => void;
    hide: () => void;
    setSelectedVehicleIndex: (index: string) => void;
}

const AiVehicleRecommendationContext = createContext<AiVehicleRecommendationContextValue | undefined>(undefined);

const PRIMARY_VEHICLE = '-1';

export const AiVehicleRecommendationProvider: FC<PropsWithChildren> = ({ children }) => {
    const dispatch = useDispatch();
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const isLocked = dealLockHooks.useIsDealLocked();

    const [isOpen, setIsOpen] = useState(false);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const [scopeId, setScopeId] = useState<string>('');
    const [selectedVehicleIndex, setSelectedVehicleIndex] = useState<string>('-1');

    const currentVehicle = useSelector(getVehicle);

    const showRecommendations = useCallback(
        (vehicleList: any[], scope: string) => {
            dispatch(clickAlternativeVehicleOptionsLink(currentVehicle));
            setVehicles(vehicleList);
            setScopeId(scope);
            setSelectedVehicleIndex('-1'); // Reset to primary
            setIsOpen(true);
        },
        [currentVehicle, dispatch]
    );

    const hide = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleApplySelection = useCallback(() => {
        const recommendation = vehicles[parseInt(selectedVehicleIndex, 10)];
        if (!recommendation) return;

        // `vehicleReducer` and offer-redux's `vehicleAndPricing` both merge this payload, so a field
        // left unset here keeps the previous vehicle's value.
        const { condition, invoicePrice, listedPrice, retailPrice } = recommendation.vehicle;
        const normalizedCondition = (condition ?? '').toLowerCase();

        const recommendedVehicle = {
            ...recommendation.vehicle,
            // `createVehicleForPaymentNode` reads `type`, not the wire's uppercase `condition`.
            type:
                normalizedCondition === 'cpo'
                    ? AccelerateVehicleCondition.CERTIFIED
                    : normalizedCondition || AccelerateVehicleCondition.USED,
            // Factory invoice is the only cost basis a recommendation carries, and it is often absent -
            // leave `cost` unset rather than asserting a dealer cost of zero.
            invoiceAmount: invoicePrice,
            cost: invoicePrice || undefined,
            // Used units with no MSRP arrive as 0.
            retailPrice: retailPrice || listedPrice
        };

        const vehiclePayload = {
            ...recommendedVehicle,
            sellingPrice: listedPrice
        };

        const paymentPayload = {
            downPayment: recommendation.payment.downPayment,
            sellingPrice: listedPrice,
            retailPrice: recommendedVehicle.retailPrice,
            buyRate: recommendation.payment.buyRate,
            apr: recommendation.payment.apr,
            vehicle: recommendedVehicle
        };

        // If in Deal Central experience, clone scope and update new scope; otherwise, update current scope
        if (isDealCentralExperience) {
            const newScopeId = nanoid();
            dispatch(scopedActionsCreators.cloneScope(scopeId, {}, newScopeId));

            const updateVehicleWithSelectedRecommendationAction =
                deskingActionCreators.updateVehicleWithSelectedRecommendation(vehiclePayload);
            dispatch(scopedActions.createScopedAction(newScopeId, updateVehicleWithSelectedRecommendationAction));

            const updatePaymentDetailsAction = paymentInputActionCreators.applyRecommendedVehiclePaymentDetails(paymentPayload);
            dispatch(scopedActions.createScopedAction(newScopeId, updatePaymentDetailsAction));
        } else {
            dispatch(deskingActionCreators.updateVehicleWithSelectedRecommendation(vehiclePayload));
            dispatch(paymentInputActionCreators.applyRecommendedVehiclePaymentDetails(paymentPayload));
        }

        hide();
    }, [selectedVehicleIndex, vehicles, scopeId, isDealCentralExperience, dispatch, hide]);

    return (
        <AiVehicleRecommendationContext.Provider value={{ showRecommendations, hide, setSelectedVehicleIndex }}>
            {children}
            <SlideOut
                header={
                    <Typography variant="h3" sx={{ padding: '0 24px', gap: '8px', display: 'flex', alignItems: 'center' }}>
                        Alternative Vehicle Options
                        <AiLogoIcon data-testid="vehicle-recommendations-header-ai-logo" fontSize="24px" ariaHidden={true} />
                    </Typography>
                }
                show={isOpen}
                position="right"
                panelWidth="auto"
                data-testid="ai-vehicle-recommendation-slideout"
                sx={{
                    '.MuiPaper-root': {
                        boxShadow: 'unset'
                    },
                    '.MuiPaper-root>div': {
                        padding: '0'
                    },
                    '.MuiPaper-root.MuiDrawer-paper': {
                        width: { xs: 1, md: 445 },
                        maxWidth: '445px',
                        padding: '24px 0 0 0',
                        display: 'flex',
                        flexDirection: 'column'
                    },
                    '.ids-SlideOut-content': {
                        flex: '1 1 auto',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '0 24px 24px !important'
                    },
                    '.MuiDialogActions-root': {
                        padding: '16px 24px',
                        backgroundColor: '#f5f5f5',
                        flex: '0 0 auto'
                    },
                    '.MuiDrawer-paper .MuiPaper-root': {
                        position: 'unset'
                    },
                    '.ids-SlideOut-overflow-footer > div': {
                        backgroundColor: 'rgba(244, 244, 244, 0.96)',
                        borderTop: '1px solid #B2B2B2'
                    },
                    '.MuiPaper-root.MuiDrawer-paper>.interstate-sliding-drawer-dialog-title-container': {
                        padding: '.5rem'
                    }
                }}
                onHide={hide}
                footer={{
                    footerComponent: (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 2,
                                backgroundColor: '#f5f5f5',
                                padding: 2
                            }}
                        >
                            <Button
                                buttonStyle="secondary"
                                onClick={hide}
                                data-testid="vehicle-recommendations-footer-cancel-button"
                            >
                                Cancel
                            </Button>
                            <Button
                                buttonStyle="primary"
                                disabled={isLocked || selectedVehicleIndex === PRIMARY_VEHICLE}
                                onClick={handleApplySelection}
                                data-testid="vehicle-recommendations-footer-apply-button"
                            >
                                Apply
                            </Button>
                        </Box>
                    )
                }}
            >
                <ScopedStateProvider primary>
                    <AiVehicleRecommendationSlideout
                        vehicles={vehicles}
                        scopeId={scopeId}
                        onClose={hide}
                        selectedVehicleIndex={selectedVehicleIndex}
                        setSelectedVehicleIndex={setSelectedVehicleIndex}
                    />
                </ScopedStateProvider>
            </SlideOut>
        </AiVehicleRecommendationContext.Provider>
    );
};

export const useAiVehicleRecommendationSlideout = () => {
    const context = useContext(AiVehicleRecommendationContext);
    if (!context) {
        throw new Error('useAiVehicleRecommendationSlideout must be used within an AiVehicleRecommendationProvider');
    }
    return context;
};
